import React, { CSSProperties, memo, ComponentProps } from 'react';
import { Element, useFela } from '../../index';
import { IconProps } from '../../Atoms/Icon/icon';
import { WithTheme } from '../../Theme';
import { WithDmId, WithId } from '../../utils/BaseProps';

export type ListItemProps = DmListItemProps &
  Omit<ComponentProps<'li'>, keyof DmListItemProps>;

type DmListItemProps = WithDmId &
  WithId & {
    /**
     * Optionales Icon, das anstelle eines Listensymbols dargestellt werden soll.
     */
    icon?: (props: IconProps) => JSX.Element;
    /**
     * Inhalt des ListItems. Dies kann sowohl Text als auch eine verschachtelte Liste sein.
     */
    children: JSX.Element;
    /**
     * Objekt, um Styling des ListItems anzupassen.
     * Davon darf nur im Ausnahmefall Gebrauch gemacht werden!
     */
    style?: CSSProperties | object;
  };

const defaultStyle = ({
  icon,
  theme,
}: WithTheme<Pick<DmListItemProps, 'icon'>>) => {
  return {
    fontFamily: theme.typography.fontFamily.primary.main,
    fontWeight: 400,
    color: theme.palette.color3.hex,
    '&:not(:last-child)': {
      marginBottom: theme.dimension.spacing.xs.rem,
    },
    extend: [
      {
        condition: icon,
        style: {
          listStyle: 'none',
        },
      },
    ],
  };
};

const iconStyle = ({ theme }: WithTheme) => ({
  position: 'absolute',
  marginTop: '11px',
  paddingRight: theme.dimension.spacing.xxxs.rem,
  transform: 'translate(-100%, -50%)',
});

const ListItem = ({
  dmId = 'dm-list-item',
  id,
  icon,
  style,
  children,
  ...otherProps
}: ListItemProps): JSX.Element => {
  const { css } = useFela({ icon });
  const Icon = icon;
  return (
    <li data-dmid={dmId} className={css([defaultStyle, style])} {...otherProps}>
      {Icon && (
        <Element className={css(iconStyle)}>
          <Icon id={`${id}-icon`} size={20} />
        </Element>
      )}
      {children}
    </li>
  );
};

export default memo(ListItem);
